import { Leaf } from 'lucide-react';

interface EmissionsExportProps {
    /** Values forwarded directly from EnvironmentalImpact page — no re-fetch */
    estimatedCO2Kg: number;
    co2ChangeVsPreviousPeriodKg: number;
    co2ChangeVsPreviousPeriodPercent: number | null;
    avgIdleMinutesPerTruck: number;
    totalAppointments: number;
    totalIdleMinutes: number;
    /** Context passed from parent */
    range: string;           // '7d' | '14d' | '30d'
    rangeLabel: string;      // 'Last 7 Days' | 'Last 14 Days' | 'Last 30 Days'
    startDate: Date;         // start of reporting window
    endDate: Date;           // end of reporting window
    facilityName: string;    // resolved warehouse name
    onClose: () => void;
}

function formatDate(d: Date): string {
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function changeLabel(changeKg: number, percent: number | null): string {
    if (percent === null) return 'No prior-period baseline available';
    const sign = changeKg <= 0 ? '↓ Reduced' : '↑ Increased';
    const pct = `${Math.abs(percent)}%`;
    return `${sign} ${Math.abs(changeKg)} kg (${pct}) vs previous period`;
}

const co2PerVisit = (kg: number, appts: number): string =>
    appts === 0 ? '0 kg' : `${(Math.round((kg / appts) * 10) / 10)} kg`;

export function EnvironmentalImpactExport({
    estimatedCO2Kg,
    co2ChangeVsPreviousPeriodKg,
    co2ChangeVsPreviousPeriodPercent,
    avgIdleMinutesPerTruck,
    totalAppointments,
    totalIdleMinutes,
    range: _range,
    rangeLabel,
    startDate,
    endDate,
    facilityName,
    onClose,
}: EmissionsExportProps) {

    const handlePrint = () => window.print();

    return (
        /* Outer wrapper uses the same CSS isolation class as MasterBOLModal */
        <div className="fixed inset-0 z-[400] flex items-center justify-center bol-print-wrapper">

            {/* Backdrop — visible in screen, gone when printing */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm print:hidden"
                onClick={onClose}
            />

            {/* Document card */}
            <div className="relative w-full max-w-2xl max-h-[92vh] print:mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col print:max-h-none print:rounded-none print:shadow-none print:overflow-visible print:max-w-none">

                {/* Toolbar — screen only */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-slate-50 print:hidden">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                        <Leaf size={15} className="text-emerald-500" />
                        Environmental Impact Summary
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrint}
                            title="For clean PDF export: disable 'Headers and footers' in your browser print settings."
                            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
                        >
                            Print / Save PDF
                        </button>
                        <button
                            onClick={onClose}
                            className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {/* Scrollable content area */}
                <div className="flex-1 overflow-y-auto print:overflow-visible">
                    <div className="bol-print p-10 space-y-7 text-slate-900 print:text-black print:bg-white print:p-0 print:space-y-6">

                        {/* ── Section 0: Document title ── */}
                        <div className="text-center border-b-2 border-slate-900 pb-5 print:border-black print-avoid-break">
                            <div className="flex items-center justify-center gap-2 mb-1">
                                <Leaf size={18} className="text-emerald-600 print:text-black" />
                                <h1 className="text-xl font-bold tracking-tight uppercase text-slate-900 print:text-black">
                                    Environmental Impact Summary Report
                                </h1>
                            </div>
                            <p className="text-sm text-slate-600 print:text-slate-700 font-medium mt-1">
                                Estimated Idle-Related CO₂ Impact from Truck Wait and Dock Service Time
                            </p>
                        </div>

                        {/* ── Section 1: Facility identification ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Facility Details</SectionLabel>
                            <div className="mt-3 grid grid-cols-3 gap-4 border border-slate-300 print:border-black rounded-none p-4">
                                <InfoField label="Facility" value={facilityName} />
                                <InfoField
                                    label="Reporting Period"
                                    value={`${formatDate(startDate)} – ${formatDate(endDate)} (${rangeLabel})`}
                                />
                                <InfoField label="Generated" value={formatDate(new Date())} />
                            </div>
                        </div>

                        {/* ── Section 2: KPI grid ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Environmental Impact Summary</SectionLabel>
                            <table className="w-full mt-3 text-sm border-collapse">
                                <thead>
                                    <tr className="bg-slate-100 print:bg-slate-100">
                                        <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Metric</th>
                                        <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Value</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                    <KpiRow label="Total Idle-Related CO₂ Impact" value={`${estimatedCO2Kg} kg`} />
                                    <KpiRow label="CO₂ Change vs Previous Period" value={changeLabel(co2ChangeVsPreviousPeriodKg, co2ChangeVsPreviousPeriodPercent)} />
                                    <KpiRow label="Average Idle Minutes per Truck" value={`${avgIdleMinutesPerTruck} min`} />
                                    <KpiRow label="Total Idle Minutes (All Trucks)" value={`${totalIdleMinutes} min`} />
                                    <KpiRow label="Appointments Processed" value={`${totalAppointments}`} />
                                    <KpiRow label="CO₂ per Truck Visit" value={co2PerVisit(estimatedCO2Kg, totalAppointments)} bold />
                                </tbody>
                            </table>
                        </div>

                        {/* ── Section 3: Operational interpretation ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Methodology & Interpretation</SectionLabel>
                            <p className="mt-3 text-sm text-slate-700 print:text-slate-800 leading-relaxed border border-slate-300 print:border-black p-4">
                                This report estimates truck idle-related CO₂ impact using recorded appointment lifecycle timestamps and a standard heavy-duty diesel emissions factor (EPA baseline). Results reflect operational scheduling efficiency and provide directional sustainability indicators rather than vehicle-specific emissions measurements.
                            </p>
                        </div>

                        {/* ── Section 4: Trend note ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Trend Data</SectionLabel>
                            <p className="mt-3 text-sm text-slate-600 print:text-slate-700 leading-relaxed">
                                Daily emissions trend visualization is available within the BayWhiz Environmental Impact dashboard. Historical trend charts are not included in this summary export.
                            </p>
                        </div>

                        {/* ── Footer ── */}
                        <div className="mt-8 pt-4 border-t-2 border-slate-300 print:border-black text-center print-avoid-break">
                            <p className="text-[11px] text-slate-400 print:text-slate-500">
                                Generated by <strong>BayWhiz Dock Scheduling Platform</strong>
                            </p>
                            <p className="text-[11px] text-slate-400 print:text-slate-500">
                                Environmental Impact estimates derived from appointment lifecycle analytics
                            </p>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── Sub-components ── */
function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 print:text-slate-600 border-b border-slate-200 print:border-slate-400 pb-1">
            {children}
        </h2>
    );
}

function InfoField({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 print:text-slate-500 mb-0.5">{label}</div>
            <div className="text-sm font-semibold text-slate-900 print:text-black">{value}</div>
        </div>
    );
}

function KpiRow({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) {
    return (
        <tr>
            <td className={`px-4 py-2.5 text-slate-700 print:text-slate-800 border border-slate-200 print:border-black ${bold ? 'font-bold' : ''}`}>{label}</td>
            <td className={`px-4 py-2.5 text-right text-slate-900 print:text-black border border-slate-200 print:border-black font-mono ${bold ? 'font-bold' : ''}`}>{value}</td>
        </tr>
    );
}
